<template>
  <div v-if="loading" class="flex h-full justify-center items-center">
    <loading-spinner diameter="64px" />
  </div>

  <alert-badge
    v-else-if="error.title"
    isDanger
    :title="error.title"
    :body="error.body"
    @dismissed="
      () => {
        error.title = ''
        error.body = ''
      }
    "
  />

  <div v-else>
    <div class="flex flex-row">
      <div class="flex flex-col">
        <action-button :title="t('actions.print')" faIcon="print" @click="print" />
      </div>
      <div class="flex flex-col items-center">
        <div :id="$ctx.getTransactionExpenseSize() == 'a4' ? 'a4-receipt': 'small-receipt'" class="w-full mt-3 pt-8">
          <el-table
            ref="table1"
            stripe
            :data="detailRows"
          >
            <el-table-column prop="label" :label="$t('models.common.label')"></el-table-column>
            <el-table-column prop="value" :label="$t('models.common.value')"></el-table-column>
          </el-table>

          <el-divider content-position="center">{{ t('associatedAccounts') }}</el-divider>

          <el-table :data="postings" style="width: 100%" :row-class-name="colorRows" max-height="700">
            <el-table-column prop="account" :label="t('account')">
              <template #default="scope">
                <el-link
                  @click.prevent="
                    $router.push(
                      $Route.ACCOUNTING_ACCOUNTS_ACCOUNT.replace(':id', scope.row.account.id)
                    )
                  "
                >
                  {{ $ctx.locale === 'ar' ? scope.row.account.arName : scope.row.account.enName }}
                </el-link>
              </template>
            </el-table-column>

            <el-table-column prop="type" :label="t('postingType')" align="center">
              <template #default="scope">
                <i v-if="scope.row.type === 'credit'" class="el-icon-remove"></i>
                <i v-else class="el-icon-circle-plus"></i>
                <span class="mx-2">{{ t(scope.row.type) }}</span>
              </template>
            </el-table-column>

            <el-table-column prop="amountDinero" :label="t('amount')" align="center">
              <template #default="scope" dir="ltr">
                {{ formatDinero(scope.row.amountDinero) }}
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>
  </div>


</template>

<script lang="ts">
import { computed, defineComponent, onBeforeMount, ref } from 'vue'
import ActionButton from '@/components/ActionButton.vue'

import { useHTTP } from '@/plugins/http'
import { formatDinero, formatMoney } from '@/utils/money'
import { parseFormat } from '@/utils/date'

import { Transaction, transactionI18nMessages } from '@/models/accounting/Transaction'
import { Expense } from '@/views/accounting/company/Expenses.vue'

// components
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import AlertBadge from '@/components/AlertBadge.vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { Account } from '@/models/accounting/Account'
import { Dinero } from 'dinero.js'
import { useContext } from '@/plugins/context'

interface Posting {
  account: Account
  type: string
  amountDinero: Dinero
}

export default defineComponent({
  name: 'transaction',
  components: {
    LoadingSpinner,
    AlertBadge,
    ActionButton
  },
  setup() {
    const transaction = ref(new Transaction())
    const expenseWhatsFor = ref('')
    const http = useHTTP()
    const loading = ref(true)
    const error = ref({ title: '', body: '' })
    const route = useRoute()
    const postings = ref(new Array<Posting>())
    const ctx = useContext()

    const { t } = useI18n({
      messages: transactionI18nMessages,
      useScope: 'global'
    })

    const isExpenseTransaction = computed(() => transaction.value.label === 'expense')

    const detailRows = computed(() => {
      const rows: Array<{ label: string; value: string }> = [
        { label: t('models.common.id') as string, value: String(transaction.value.externalID || '') },
        {
          label: t('models.common.createdAt') as string,
          value: parseFormat(transaction.value.createdAt)
        },
        { label: t('amount'), value: formatDinero(transaction.value.amountDinero) },
        { label: t('user'), value: transaction.value.user.fullName },
        {
          label: t('location'),
          value:
            ctx.locale === 'en'
              ? transaction.value.location.enName
              : transaction.value.location.arName
        }
      ]

      if (isExpenseTransaction.value) {
        rows.push({ label: t('whatsFor'), value: expenseWhatsFor.value || '' })
        rows.push({ label: t('notes'), value: transaction.value.description })
      } else {
        rows.push({ label: t('description'), value: transaction.value.description })
      }

      const breakdown = transaction.value.multiCurrencyBreakdown as Array<{
        amount: number
        currency: string
      }>
      rows.push({
        label: t('multiCurrency'),
        value:
          !breakdown || breakdown.length != 2
            ? ''
            : `${formatMoney(breakdown[0].amount, breakdown[0].currency)}
              + ${formatMoney(breakdown[1].amount, breakdown[1].currency)}`
      })

      return rows
    })

    onBeforeMount(async () => {
      const url = `${Transaction.ENDPOINT}/${route.params.id}`

      try {
        const resp = await http.get<Transaction>(url)
        transaction.value = Transaction.from(resp)

        postings.value = [
          {
            account: transaction.value.creditedAccount,
            type: 'credit',
            amountDinero: transaction.value.amountDinero
          },
          {
            account: transaction.value.debitedAccount,
            type: 'debit',
            amountDinero: transaction.value.amountDinero
          }
        ]

        if (transaction.value.label === 'expense' && transaction.value.referenceableID) {
          try {
            const expense = await http.get<Expense>(
              `/accounting/company/${transaction.value.referenceableID}`
            )
            expenseWhatsFor.value = expense.whatsFor || ''
          } catch {
            expenseWhatsFor.value = ''
          }
        }
      } catch (errorResp: any) {
        error.value.title = errorResp?.title
        error.value.body = errorResp?.body
      }

      loading.value = false
    })

    return {
      transaction,
      loading,
      error,
      t,
      postings,
      detailRows
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
    this.handleResize();
  },
  methods: {
    formatMoney,
    parseFormat,
    formatDinero,
    colorRows(params: { row: Posting }) {
      if (params.row.type === 'credit') {
        return 'bg-danger'
      }

      return 'bg-success'
    },
    beforeDestroy() {
      window.removeEventListener('resize', this.handleResize);
    },
    print() {
      window.print();
    },
    handleResize() {
      this.$nextTick(() => {
        /* @ts-ignore */
        this.$refs['table1'].doLayout();
      });
    }
  }
})
</script>
